import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { setEphemeralRunId } from '../api/client';
import {
  launchReckoning as launchReckoningApi,
  fetchReckoningState as fetchReckoningStateApi,
  fetchReckoningEligibility as fetchReckoningEligibilityApi,
  forfeitReckoning as forfeitReckoningApi,
} from '../api/reckonings';

const STORAGE_KEY = 'debono_active_reckoning_v1';

const ReckoningContext = createContext({
  reckoning: null,
  eligibility: null,
  activeChildRunId: null,
  isReckoningActive: false,
  launch: async () => {},
  refresh: async () => {},
  forfeit: async () => {},
  clear: () => {},
  setActiveChild: () => {},
  refreshEligibility: async () => {},
});

function readPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.reckoning_id) return parsed;
    return null;
  } catch {
    return null;
  }
}

function writePersisted(reckoning) {
  try {
    if (reckoning && reckoning.reckoning_id) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(reckoning));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable — context still in memory
  }
}

export function ReckoningProvider({ children }) {
  const [reckoning, setReckoning] = useState(() => readPersisted());
  const [eligibility, setEligibility] = useState(null);
  const [activeChildRunId, setActiveChildRunIdState] = useState(null);
  const refreshLockRef = useRef(false);
  const hydrateLockRef = useRef(null);

  const applyActiveChildRunId = useCallback((runId) => {
    const nextRunId = runId || null;
    setEphemeralRunId(nextRunId);
    setActiveChildRunIdState(nextRunId);
  }, []);

  useEffect(() => {
    setEphemeralRunId(activeChildRunId || null);
  }, [activeChildRunId]);

  useEffect(() => {
    writePersisted(reckoning);
    if (!reckoning) {
      setActiveChildRunIdState(null);
    }
  }, [reckoning]);

  useEffect(() => () => setEphemeralRunId(null), []);

  const hydrateActiveReckoning = useCallback(async (reckoningId) => {
    if (!reckoningId) return null;
    if (hydrateLockRef.current?.reckoningId === reckoningId) {
      return hydrateLockRef.current.promise;
    }
    const hydratePromise = (async () => {
      const fresh = await fetchReckoningStateApi(reckoningId);
      if (fresh?.status === 'active' && fresh?.reckoning_id) {
        setReckoning(fresh);
        if (Array.isArray(fresh.decks) && fresh.decks.length > 0) {
          applyActiveChildRunId(fresh.decks[0].run_id);
        }
        return fresh;
      }
      return null;
    })();
    hydrateLockRef.current = { reckoningId, promise: hydratePromise };
    try {
      return await hydratePromise;
    } catch {
      return null;
    } finally {
      if (hydrateLockRef.current?.promise === hydratePromise) {
        hydrateLockRef.current = null;
      }
    }
  }, [applyActiveChildRunId]);

  const refreshEligibility = useCallback(async () => {
    try {
      const data = await fetchReckoningEligibilityApi();
      setEligibility(data);
      const activeReckoningId = data?.active_reckoning?.reckoning_id;
      if (activeReckoningId && !reckoning?.reckoning_id) {
        const hydrated = await hydrateActiveReckoning(activeReckoningId);
        if (hydrated) return { ...data, hydrated_reckoning: hydrated };
      }
      return data;
    } catch {
      setEligibility(null);
      return null;
    }
  }, [reckoning?.reckoning_id, hydrateActiveReckoning]);

  const clear = useCallback(() => {
    setReckoning(null);
    applyActiveChildRunId(null);
    refreshEligibility();
  }, [applyActiveChildRunId, refreshEligibility]);

  const refresh = useCallback(async () => {
    if (!reckoning?.reckoning_id) return null;
    if (refreshLockRef.current) return reckoning;
    refreshLockRef.current = true;
    try {
      const fresh = await fetchReckoningStateApi(reckoning.reckoning_id);
      if (fresh?.status && fresh.status !== 'active') {
        setReckoning(null);
        applyActiveChildRunId(null);
        refreshEligibility();
        return fresh;
      }
      setReckoning((prev) => ({ ...(prev || {}), ...fresh }));
      return fresh;
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 410) {
        clear();
      }
      return null;
    } finally {
      refreshLockRef.current = false;
    }
  }, [reckoning, applyActiveChildRunId, clear, refreshEligibility]);

  useEffect(() => {
    if (!reckoning?.reckoning_id) return;
    refresh();
    // Verify a persisted active Reckoning once per parent id on mount/reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reckoning?.reckoning_id]);

  const launch = useCallback(async () => {
    const result = await launchReckoningApi();
    const activeResult = { status: 'active', ...result };
    setReckoning(activeResult);
    if (Array.isArray(activeResult?.decks) && activeResult.decks.length > 0) {
      applyActiveChildRunId(activeResult.decks[0].run_id);
    }
    refreshEligibility();
    if (activeResult?.reckoning_id) {
      try {
        const fresh = await fetchReckoningStateApi(activeResult.reckoning_id);
        if (fresh?.status === 'active') {
          setReckoning(fresh);
          return fresh;
        }
      } catch {
        // Launch response is enough to show the tab; Dashboard will retry hydration.
      }
    }
    return activeResult;
  }, [applyActiveChildRunId, refreshEligibility]);

  const forfeit = useCallback(async () => {
    const current = reckoning;
    setReckoning(null);
    applyActiveChildRunId(null);
    if (current?.reckoning_id) {
      try {
        await forfeitReckoningApi(current.reckoning_id);
      } catch {
        // Ignore — local state already cleared
      }
    }
    refreshEligibility();
  }, [reckoning, applyActiveChildRunId, refreshEligibility]);

  const setActiveChild = useCallback((runId) => {
    applyActiveChildRunId(runId || null);
  }, [applyActiveChildRunId]);

  const isReckoningActive = !!reckoning?.reckoning_id && reckoning.status === 'active';

  const value = useMemo(() => ({
    reckoning,
    eligibility,
    activeChildRunId,
    isReckoningActive,
    launch,
    refresh,
    forfeit,
    clear,
    setActiveChild,
    refreshEligibility,
  }), [reckoning, eligibility, activeChildRunId, isReckoningActive, launch, refresh, forfeit, clear, setActiveChild, refreshEligibility]);

  return (
    <ReckoningContext.Provider value={value}>
      {children}
    </ReckoningContext.Provider>
  );
}

export function useReckoning() {
  return useContext(ReckoningContext);
}
